import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { useNavigate } from "react-router-dom";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { MagicLink, Unit } from "types/landlord/address";
import { FC, useMemo } from "react";
import { useAuthContext } from "providers/Auth";
import { Switch } from "shared/components/forms/Switch";
import { unitStatus } from "shared/app/UnitsTableView/constants";
import { Badge, BadgeSize, BadgeVariant, Button } from "shared/components";
import { Table } from "shared/app/Table";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { toMoney } from "utils/currency";
import { useSettingsController } from "views/settings-developers/controller";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { Door } from "providers/Layout/components/Door";
import { CurrencyDollar, Database01 } from "untitledui-js-base";

type Props = {
  data: Unit[];
  handleCopyLink: (unit: Unit) => void;
  refetch: () => void;
  setSelectedUnitId: (id: number) => void;
  setShowInviteApplicantsModal: (show: boolean) => void;
  setShowDeleteUnitModal: (show: boolean) => void;
  hasSearchParams?: boolean;
  hideProperty?: boolean;
  isLoading?: boolean;
};

export const UnitsTable: FC<Props> = ({
  data,
  handleCopyLink,
  refetch,
  setSelectedUnitId,
  setShowInviteApplicantsModal,
  setShowDeleteUnitModal,
  hasSearchParams,
  hideProperty,
  isLoading
}) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();
  const { isEverySyncEnabled } = useSettingsController();

  const columns = useMemo(() => {
    const cols: ColumnDef<any, any>[] = [
      {
        accessorKey: "title",
        header: "Unit",
        cell: (p) => (
          <p className="font-medium text-content-1000 max-w-[460px] truncate">
            {p.getValue()}
          </p>
        )
      }
    ];

    if (!hideProperty)
      cols.push({
        accessorFn: (row) => getShortAddressFromAddress(row.property.address),
        header: "Property",
        cell: (p) => {
          const unit = p.row.original;
          return (
            <>
              <p className="overflow-hidden max-w-[460px] text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                {unit?.property?.name}
              </p>
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-400">
                {p.getValue()}
              </p>
            </>
          );
        }
      });

    cols.push({
      accessorKey: "occupied",
      header: "Current Status",
      cell: (p) => {
        const row = p.row.original;
        const occupied = p.getValue();
        return (
          <div>
            <Badge
              size={BadgeSize.SM}
              variant={occupied ? BadgeVariant.PRIMARY : BadgeVariant.SUCCESS}
            >
              {unitStatus(occupied)}
            </Badge>
            {!row.pms_occupied_override && row.pms_occupied !== occupied ? (
              <p className="text-xs text-content-400">Manually overridden</p>
            ) : null}
          </div>
        );
      }
    });

    if (type.isPMS)
      cols.push({
        accessorKey: "pms_id",
        header: "Source",
        cell: (p) => (
          <p className="font-medium text-content-1000">
            {p.getValue() ? "PMS" : "Manually added"}
          </p>
        )
      });

    cols.push({
      accessorKey: "monthly_rent_amount",
      header: "Rent Amount",
      cell: (p) => {
        const row = p.row.original;
        return (
          <>
            <p className="font-medium text-content-1000">
              {p.getValue()?.cents
                ? toMoney(p.getValue().cents / 100, 0)
                : "--"}
            </p>
            {!row.pms_monthly_rent_amount_override &&
            row.pms_monthly_rent_amount?.cents !== p.getValue()?.cents ? (
              <p className="text-xs text-content-400">Manually overridden</p>
            ) : null}
          </>
        );
      }
    });

    if (type.isBoomScreen)
      cols.push(
        {
          accessorKey: "magic_link",
          header: "Allow applications",
          size: 120,
          cell: (p) => {
            const magicLinkData = p.row.original.magic_link as MagicLink | null;
            const active = magicLinkData?.active || false;
            const id = magicLinkData?.id || 0;
            return isEverySyncEnabled && p.row.original.pms_id ? (
              <AllowOrDisallowApplications
                isActive={p.row.original.magic_link?.active}
                withText={false}
              />
            ) : (
              <Switch
                checked={active}
                onChange={(active) => {
                  (async () => {
                    try {
                      await addressApi.updateMagicLink(id, { active });
                      await refetch();
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    }
                  })();
                }}
              />
            );
          }
        },
        {
          id: "popover-action",
          header: "",
          size: 32,
          cell: (p) => {
            const unit = p.row.original;

            const actions = [
              {
                title: "Review",
                onClick: (e) => {
                  e.stopPropagation();
                  navigate(
                    `/properties/${unit?.property?.id}/units/${unit?.id}`
                  );
                }
              },
              {
                title: "Invite to unit",
                onClick: (e) => {
                  e.stopPropagation();
                  if (p.row.original.magic_link?.active) {
                    setSelectedUnitId(unit?.id);
                    setShowInviteApplicantsModal(true);
                  }
                },
                disabled: !p.row.original.magic_link?.active,
                tooltip:
                  "You can invite applicants to a unit that accepts applications",
                tooltipPlacement: "left" as const
              },
              {
                title: "Copy application link",
                onClick: (e) => {
                  e.stopPropagation();
                  if (p.row.original.magic_link?.active) {
                    setSelectedUnitId(unit?.id);
                    handleCopyLink(unit);
                  }
                },
                disabled: !p.row.original.magic_link?.active,
                tooltip: "Can't copy invite link on INACTIVE unit"
              },
              {
                title: "Delete unit",
                onClick: (e) => {
                  e.stopPropagation();
                  if (unit.can_delete && !unit.pms_id) {
                    setShowDeleteUnitModal(true);
                    setSelectedUnitId(unit?.id);
                  }
                },
                color:
                  unit.can_delete && !unit.pms_id
                    ? "text-danger-500"
                    : "text-content-100",
                disabled: !unit.can_delete || unit.pms_id,
                tooltip: unit.pms_id
                  ? "You can't delete a PMS unit"
                  : !unit?.can_delete
                    ? "You can't delete a unit with applications"
                    : undefined
              }
            ];

            return <ContextMenuPopover actions={actions} />;
          }
        }
      );
    else
      cols.push({
        id: "popover-action",
        header: "",
        cell: (p) => {
          const unit = p.row.original;

          const actions = [
            {
              title: "Review",
              onClick: (e) => {
                e.stopPropagation();
                navigate(`/properties/${unit?.property?.id}/units/${unit?.id}`);
              }
            },
            {
              title: "Delete unit",
              onClick: (e) => {
                e.stopPropagation();
                setShowDeleteUnitModal(true);
                setSelectedUnitId(unit?.id);
              },
              color:
                unit.can_delete || !type.isPMS
                  ? "text-content-100"
                  : "text-danger-500",
              disabled: !unit.can_delete || !type.isPMS,
              tooltip: type.isPMS
                ? "You can't delete a unit"
                : "You can't delete a unit with applications"
            }
          ];

          return <ContextMenuPopover actions={actions} />;
        }
      });

    return cols;
  }, [type.isBoomScreen, isEverySyncEnabled]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table
      rowHeight="64px"
      thClassName=""
      tdClassName="!py-2"
      emptyStateTitle={
        hasSearchParams ? "No results found" : "Units will appear here"
      }
      isLoading={isLoading}
      emptyStateText={
        hasSearchParams
          ? "We couldn't find any data matching your query"
          : "Add units to the property"
      }
      table={table}
      onRowClick={(id, rowData) => {
        navigate(`/properties/${rowData.original?.property?.id}/units/${id}`);
      }}
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      mobileRow={(row) => {
        return (
          <div className="">
            <div className="flex justify-between -mb-2">
              <div className="">
                {(() => {
                  const magicLinkData = row.original
                    .magic_link as MagicLink | null;
                  const active = magicLinkData?.active || false;
                  const id = magicLinkData?.id || 0;
                  return isEverySyncEnabled && row.original.pms_id ? (
                    <AllowOrDisallowApplications
                      isActive={row.original.magic_link?.active}
                      withText={false}
                    />
                  ) : (
                    <Switch
                      checked={active}
                      onChange={(active) => {
                        (async () => {
                          try {
                            await addressApi.updateMagicLink(id, { active });
                            await refetch();
                          } catch (err) {
                            toast.error(getErrorMessage(err));
                          }
                        })();
                      }}
                    />
                  );
                })()}
              </div>

              <div className="translate-x-3 -translate-y-3">
                <ContextMenuPopover
                  actions={[
                    {
                      title: "Review",
                      onClick: () => {
                        navigate(
                          `/properties/${row.original?.property?.id}/units/${row.original?.id}`
                        );
                      }
                    },
                    {
                      title: "Invite to unit",
                      onClick: () => {
                        if (row.original.magic_link?.active) {
                          setSelectedUnitId(row.original.id);
                          setShowInviteApplicantsModal(true);
                        }
                      },
                      disabled: !row.original.magic_link?.active,
                      tooltip: "Can't invite to INACTIVE unit"
                    },
                    {
                      title: "Delete unit",
                      onClick: () => {
                        if (row.original.can_delete && !row.original.pms_id) {
                          setShowDeleteUnitModal(true);
                          setSelectedUnitId(row.original.id);
                        }
                      },
                      color:
                        row.original.can_delete && !row.original.pms_id
                          ? "text-danger-500"
                          : "text-content-100",
                      disabled: !row.original.can_delete || row.original.pms_id,
                      tooltip: row.original.pms_id
                        ? "You can't delete a PMS unit"
                        : !row.original?.can_delete
                          ? "You can't delete a unit with applications"
                          : undefined
                    }
                  ]}
                />
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-bold text-base text-content-1000">
                {row.original.title}
              </p>
              <p className="font-normal text-sm text-content-500">
                {getShortAddressFromAddress(row.original.property.address)}
              </p>
            </div>

            <div className="flex flex-col gap-1.5 py-4 my-4 border-y border-alpha-40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Door className="shrink-0 stroke-content-500" size="20" />
                  <p className="font-medium text-sm text-content-500">
                    Current status
                  </p>
                </div>

                <Badge
                  size={BadgeSize.SM}
                  variant={
                    row.original.occupied
                      ? BadgeVariant.PRIMARY
                      : BadgeVariant.SUCCESS
                  }
                >
                  {unitStatus(row.original.occupied)}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CurrencyDollar
                    className="shrink-0 stroke-content-500"
                    size="20"
                  />
                  <p className="font-medium text-sm text-content-500">
                    Rent amount
                  </p>
                </div>

                <p className="font-medium text-sm text-content-500">
                  {row.original.monthly_rent_amount?.cents
                    ? toMoney(row.original.monthly_rent_amount.cents / 100, 0)
                    : "$--"}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database01
                    className="shrink-0 stroke-content-500"
                    size="20"
                  />
                  <p className="font-medium text-sm text-content-500">Source</p>
                </div>

                <p className="font-medium text-sm text-content-500">
                  {row.original.pms_id ? "PMS" : "Manually added"}
                </p>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyLink(row.original);
              }}
            >
              Copy application link
            </Button>
          </div>
        );
      }}
    />
  );
};
